import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import { safeStorage } from '../utils/safeStorage';
import { clearPendingAuthChallenge } from '../utils/pendingAuthChallenge';
import { isNativeApp } from '../utils/isNativeApp';
import { getExistingAnonId } from '../utils/anonId';
import {
  initDeepLinkHandler,
  consumePendingDeepLink,
  handleWebDeepLink,
} from '../utils/deepLinkService';

const GUEST_CLAIM_RETRY_KEY = 'gweffit_guest_claim_retry';
const GUEST_CLAIM_RETRY_COOLDOWN_MS = 30_000;

function readGuestClaimRetryState() {
  try {
    const raw = safeStorage.get(GUEST_CLAIM_RETRY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function writeGuestClaimRetryState(nextState) {
  try {
    if (!nextState) {
      safeStorage.remove(GUEST_CLAIM_RETRY_KEY);
      return;
    }
    safeStorage.set(GUEST_CLAIM_RETRY_KEY, JSON.stringify(nextState));
  } catch {}
}

function buildGuestUser() {
  return {
    id: null,
    username: 'Guest',
    email: null,
    points: 0,
    streak: 0,
    coins: 1000,
    trialsJoined: 0,
    isPremium: false,
    ownedAvatars: ['default'],
    selectedAvatar: 'default',
  };
}

function shouldSkipGuestClaimAttempt(retryState) {
  const lastAttemptAt = retryState?.last_attempt_at ? Date.parse(retryState.last_attempt_at) : NaN;
  if (!Number.isFinite(lastAttemptAt)) return false;
  return (Date.now() - lastAttemptAt) < GUEST_CLAIM_RETRY_COOLDOWN_MS;
}

export default function useAppBootstrap({
  user,
  authUser,
  navigate,
  withTimeout,
  loadUserFromAuth,
  logHealthSnapshot,
  trackEvent,
  setUser,
  setUserLoading,
  setAuthUser,
  setShowAuthModal,
  userIdRef,
  authUserIdRef,
  isLoadingUserRef,
  lastBootstrappedAuthUserRef,
}) {
  const [guestClaimPending, setGuestClaimPending] = useState(() => Boolean(readGuestClaimRetryState()));
  const guestClaimInFlightRef = useRef(false);
  const getAuthStateRef = useRef(() => ({ isAuthenticated: false }));
  const loadUserFromAuthRef = useRef(loadUserFromAuth);
  const attemptGuestActivityClaimRef = useRef(null);
  const logHealthSnapshotRef = useRef(logHealthSnapshot);
  const trackEventRef = useRef(trackEvent);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    loadUserFromAuthRef.current = loadUserFromAuth;
  }, [loadUserFromAuth]);

  useEffect(() => {
    logHealthSnapshotRef.current = logHealthSnapshot;
  }, [logHealthSnapshot]);

  useEffect(() => {
    trackEventRef.current = trackEvent;
  }, [trackEvent]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    getAuthStateRef.current = () => ({ isAuthenticated: !!user?.id });
  }, [user?.id]);

  const attemptGuestActivityClaim = useCallback(async (reason = 'unknown') => {
    if (guestClaimInFlightRef.current) return { ok: false, skipped: true, reason: 'inflight' };

    const retryState = readGuestClaimRetryState();
    const anonId = retryState?.anon_id || getExistingAnonId();
    if (!anonId || !authUser?.id || !user?.id) {
      if (!retryState) setGuestClaimPending(false);
      return { ok: false, skipped: true, reason: 'missing_prerequisites' };
    }
    if (shouldSkipGuestClaimAttempt(retryState)) {
      return { ok: false, skipped: true, reason: 'cooldown' };
    }

    guestClaimInFlightRef.current = true;
    setGuestClaimPending(true);
    writeGuestClaimRetryState({
      anon_id: anonId,
      first_failed_at: retryState?.first_failed_at || null,
      last_attempt_at: new Date().toISOString(),
      reason,
    });
    console.log('[guest_claim_retry_attempt]', {
      reason,
      anon_id_present: Boolean(anonId),
      auth_user_id: authUser.id,
      app_user_id: user.id,
    });

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        throw new Error('missing_access_token');
      }

      const response = await supabase.functions.invoke('claim-anon-activity', {
        body: { anon_id: anonId },
      });

      if (response.error) {
        throw response.error;
      }

      console.log('✅ Claimed votes:', response.data);
      trackEvent('anon_claim_completed', {
        claimed: response.data?.claimed ?? 0,
        skipped: response.data?.skipped ?? 0,
      });
      writeGuestClaimRetryState(null);
      setGuestClaimPending(false);
      return { ok: true, data: response.data };
    } catch (error) {
      console.error('❌ Failed to claim guest activity:', error);
      writeGuestClaimRetryState({
        anon_id: anonId,
        first_failed_at: retryState?.first_failed_at || new Date().toISOString(),
        last_attempt_at: new Date().toISOString(),
        reason,
        error: error?.message || String(error),
      });
      setGuestClaimPending(true);
      return { ok: false, error };
    } finally {
      guestClaimInFlightRef.current = false;
    }
  }, [authUser, user, trackEvent]);

  useEffect(() => {
    attemptGuestActivityClaimRef.current = attemptGuestActivityClaim;
  }, [attemptGuestActivityClaim]);

  useEffect(() => {
    const onAuthRequired = (parsedLink) => {
      console.log('[DeepLink] Auth required for:', parsedLink);
      setShowAuthModal(true);
    };

    initDeepLinkHandler((...args) => navigateRef.current(...args), () => getAuthStateRef.current(), onAuthRequired);
    handleWebDeepLink((...args) => navigateRef.current(...args), window.location);
  }, [setShowAuthModal]);

  useEffect(() => {
    if (user?.id) {
      const pending = consumePendingDeepLink();
      if (pending) {
        console.log('[DeepLink] Resuming pending navigation after auth:', pending.route);
        navigateRef.current(pending.route, {
          state: {
            fromDeepLink: true,
            deepLinkParams: pending.params,
            deepLinkType: pending.type,
          }
        });
      }
    }
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;

    trackEvent('app_open', {
      referrer: document.referrer || 'direct',
      path: window.location.pathname,
      search: window.location.search
    });

    async function initUser() {
      setUserLoading(true);
      const bootstrapStartedAt = Date.now();
      console.log('[bootstrap_start]', {
        source: 'initUser',
        appState: isNativeApp() ? 'native' : 'web',
        visibilityState: document.visibilityState,
      });

      try {
        const hash = window.location.hash || '';
        const hashParams = new URLSearchParams(hash.substring(1));

        const search = window.location.search || '';
        const searchParams = new URLSearchParams(search);

        const { data } = await withTimeout(
          supabase.auth.getSession(),
          10_000,
          'bootstrap_get_session_timeout'
        );
        const session = data?.session ?? null;

        if (session?.user) {
          console.log('[initUser] Found auth session for', session.user.id);
          authUserIdRef.current = session.user.id;
          try {
            const hasToken =
              searchParams.get('access_token') ||
              hashParams.get('access_token') ||
              searchParams.get('refresh_token') ||
              hashParams.get('refresh_token');
            if (hasToken) {
              window.history.replaceState({}, '', window.location.pathname);
            }
          } catch {}
          setAuthUser(session.user);
          return;
        }

        console.log('[initUser] No auth session');
        const onCrewJoinPage = window.location.pathname.includes('/crew/join/');
        const hasAuthTokensInUrl =
          searchParams.get('access_token') || hashParams.get('access_token');

        if (!onCrewJoinPage && !hasAuthTokensInUrl) {
          setUser(buildGuestUser());
          setShowAuthModal(false);
        }
        console.log('[bootstrap_done]', {
          source: 'initUser',
          has_session: !!session,
          elapsed_ms: Date.now() - bootstrapStartedAt,
        });
        logHealthSnapshot('bootstrap_init_done');
      } catch (err) {
        console.error('[initUser] Error:', err);
        console.error('[bootstrap_error]', {
          source: 'initUser',
          error: err?.message || String(err),
          elapsed_ms: Date.now() - bootstrapStartedAt,
        });
        logHealthSnapshot('bootstrap_init_error');
        setUser(buildGuestUser());
        setShowAuthModal(false);
      } finally {
        if (!cancelled) setUserLoading(false);
      }
    }

    initUser();
    return () => {
      cancelled = true;
    };
  }, [
    authUserIdRef,
    logHealthSnapshot,
    setAuthUser,
    setShowAuthModal,
    setUser,
    setUserLoading,
    trackEvent,
    withTimeout,
  ]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[auth listener] Event:', event, 'User:', session?.user?.id);
        authUserIdRef.current = session?.user?.id || null;
        setAuthUser(session?.user ?? null);

        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
          if (
            lastBootstrappedAuthUserRef.current === session.user.id &&
            !!userIdRef.current
          ) {
            console.log('[bootstrap] skipped_same_user', {
              source: 'auth_listener',
              event,
              auth_user_id: session.user.id,
              app_user_id: userIdRef.current,
            });
            return;
          }
          console.log('[auth] SIGNED_IN - Loading user data');

          if (isLoadingUserRef.current) {
            console.log('[auth] Already loading user, skipping duplicate SIGNED_IN');
            return;
          }

          isLoadingUserRef.current = true;

          try {
            setUserLoading(true);
            console.log('[bootstrap_start]', {
              source: 'auth_listener',
              event,
              has_session: true,
              visibilityState: document.visibilityState,
            });
            const startedAt = Date.now();
            await withTimeout(
              loadUserFromAuthRef.current(session.user),
              10_000,
              'bootstrap_load_user_timeout'
            );
            console.log('[bootstrap_done]', {
              source: 'auth_listener',
              event,
              elapsed_ms: Date.now() - startedAt,
            });
            lastBootstrappedAuthUserRef.current = session.user.id;
            logHealthSnapshotRef.current(`bootstrap_auth_${event}`);
            clearPendingAuthChallenge();
            setShowAuthModal(false);
            console.log('✅ User loaded from auth listener');

            trackEventRef.current('auth_completed', { method: 'email_otp' });
            void attemptGuestActivityClaimRef.current?.('auth_listener');

            const returnUrl = safeStorage.get('gweffit_return_url');
            if (returnUrl) {
              safeStorage.remove('gweffit_return_url');
              console.log('🔄 Redirecting to return URL:', returnUrl);
              window.location.href = returnUrl;
            }
          } catch (err) {
            console.error('❌ Auth listener error:', err);
            console.error('[bootstrap_error]', {
              source: 'auth_listener',
              event,
              error: err?.message || String(err),
            });
            logHealthSnapshotRef.current(`bootstrap_auth_${event}_error`);
            setUser(buildGuestUser());
            setShowAuthModal(true);
          } finally {
            setUserLoading(false);
            isLoadingUserRef.current = false;
          }
        } else if (event === 'TOKEN_REFRESHED') {
          console.log('[auth] Token refreshed - user already loaded');
        } else if (event === 'SIGNED_OUT') {
          console.log('[auth] User signed out, returning to guest mode');
          lastBootstrappedAuthUserRef.current = null;
          clearPendingAuthChallenge();
          setUser(buildGuestUser());
          setShowAuthModal(false);
          setUserLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [
    authUserIdRef,
    isLoadingUserRef,
    lastBootstrappedAuthUserRef,
    setAuthUser,
    setShowAuthModal,
    setUser,
    setUserLoading,
    userIdRef,
    withTimeout,
  ]);

  useEffect(() => {
    if (authUser && user?.id) {
      console.log('[auth] Authenticated app user present, forcing auth modal closed');
      setShowAuthModal(false);
    }
  }, [authUser, user?.id, setShowAuthModal]);

  useEffect(() => {
    if (!authUser?.id || !user?.id) return;
    const retryState = readGuestClaimRetryState();
    if (!retryState?.anon_id) {
      setGuestClaimPending(false);
      return;
    }
    attemptGuestActivityClaim('authenticated_boot');
  }, [authUser?.id, user?.id, attemptGuestActivityClaim]);

  return {
    guestClaimPending,
    attemptGuestActivityClaim,
  };
}
